// ============================================================================
// api_deviceWhip.ts — côté DEVICE (page /webrtc/:uuid)
//
// Mode SFU : quand auth_success contient un bloc "sfu", le device publie sa
// caméra+micro en WHIP sur son path MediaMTX src_{uuid}_{deviceId} (au lieu
// de la négociation P2P offer/answer relayée par la WS), et lit le retour
// programme en WHEP sur program_whep_url (au lieu de la piste "return" P2P).
//
// La WS de signalisation reste ouverte : auth, présence, intercom, on_air.
// H264 est forcé via forceSdpCodecs — indispensable pour servir l'angle en
// HLS sans transcodage (même helper que le WHIP programme de la régie).
// ============================================================================

import { forceSdpCodecs } from '../../dataTypes/liveStreamManagment/utils_sdp';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';
import { SdpLogger } from '../../dataTypes/liveStreamManagment/types_callbacks';

const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS  = 15000;

// ─── Publication WHIP (caméra device → path source MediaMTX) ─────────────────

let publishPc:      RTCPeerConnection | null = null;
let publishUrl:     string | null = null;
let publishStream:  MediaStream | null = null;
let publishRetries  = 0;
let publishStopped  = true;
let publishOnState: ((state: RTCPeerConnectionState) => void) | undefined;

/**
 * @param onConnectionStateChange  État de la PC de publication. En mode SFU c'est le
 *   seul signal de « je suis bien en train d'émettre » : il n'y a pas de PeerConnection
 *   P2P avec la régie, donc sans ce retour l'UI du device resterait sur « en attente ».
 */
export const publishDeviceWhip = async (
  stream: MediaStream,
  whipUrl: string,
  addSdpLog: SdpLogger,
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void,
): Promise<void> => {
  stopDeviceWhip(); // repartir propre (changement de caméra, reconnexion)
  publishStopped = false;
  publishUrl     = whipUrl;
  publishStream  = stream;
  publishOnState = onConnectionStateChange;
  await attemptPublish(addSdpLog);
};

const attemptPublish = async (addSdpLog: SdpLogger): Promise<void> => {
  if (publishStopped || !publishUrl || !publishStream) return;

  console.log(`[DEVICE-WHIP] ▶ Publication WHIP → ${publishUrl}`);
  addSdpLog({ direction: 'info', type: 'event', label: '▶ Publication WHIP du flux caméra (SFU)' });

  const pc = new RTCPeerConnection(defaultRTCConfig());
  publishPc = pc;

  publishStream.getTracks().forEach(track => pc.addTrack(track, publishStream!));

  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    console.log(`[DEVICE-WHIP] 🔗 ${s}`);
    if (pc !== publishPc) return; // PC remplacée
    publishOnState?.(s);
    if (s === 'connected') {
      publishRetries = 0;
      addSdpLog({ direction: 'info', type: 'event', label: '✅ WHIP connecté — caméra publiée sur le SFU' });
    }
    if (s === 'failed' || s === 'disconnected') {
      addSdpLog({ direction: 'received', type: 'error', label: `⚠ WHIP ${s} — reconnexion…` });
      scheduleRepublish(addSdpLog);
    }
  };

  try {
    const offer = await pc.createOffer();
    if (!offer.sdp) throw new Error('SDP vide');
    const filteredSdp = forceSdpCodecs(offer.sdp);
    await pc.setLocalDescription({ type: 'offer', sdp: filteredSdp });

    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check); resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 5000);
    });

    const res = await fetch(publishUrl!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription!.sdp,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`WHIP ${res.status}${body ? ': ' + body : ''}`);
    }
    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    console.log('[DEVICE-WHIP] ✅ Answer WHIP appliquée');
    addSdpLog({ direction: 'received', type: 'answer', label: 'Answer WHIP ← SFU', sdp: answerSdp });
  } catch (err: any) {
    console.warn(`[DEVICE-WHIP] ❌ ${err?.message ?? err}`);
    addSdpLog({ direction: 'received', type: 'error', label: `❌ WHIP: ${err?.message ?? err}` });
    try { pc.close(); } catch { /* silencieux */ }
    scheduleRepublish(addSdpLog);
  }
};

const scheduleRepublish = (addSdpLog: SdpLogger): void => {
  if (publishStopped) return;
  publishRetries += 1;
  const delay = Math.min(RETRY_BASE_MS * publishRetries, RETRY_MAX_MS);
  console.log(`[DEVICE-WHIP] 🔄 Nouvelle tentative dans ${delay}ms`);
  setTimeout(() => { attemptPublish(addSdpLog); }, delay);
};

/** Remplace les pistes publiées (changement caméra/micro) sans renégocier. */
export const replaceDeviceWhipTracks = async (stream: MediaStream): Promise<void> => {
  publishStream = stream;
  if (!publishPc) return;
  for (const sender of publishPc.getSenders()) {
    const kind = sender.track?.kind;
    const newTrack = kind === 'video' ? stream.getVideoTracks()[0]
                   : kind === 'audio' ? stream.getAudioTracks()[0]
                   : null;
    if (newTrack) await sender.replaceTrack(newTrack);
  }
};

export const stopDeviceWhip = (): void => {
  publishStopped = true;
  publishRetries = 0;
  if (publishPc) { try { publishPc.close(); } catch { /* silencieux */ } publishPc = null; }
  if (publishUrl) { try { fetch(publishUrl, { method: 'DELETE' }); } catch { /* silencieux */ } }
  publishUrl    = null;
  publishStream = null;
  publishOnState = undefined;
};

export const isDeviceWhipActive = (): boolean => !publishStopped && publishPc !== null;

/**
 * La PeerConnection de publication, pour qui veut en lire les statistiques.
 *
 * Exposée en LECTURE et rien d'autre : elle est recréée à chaque republication
 * (changement de caméra, reprise après coupure), donc un appelant qui la garderait
 * observerait une connexion morte sans le savoir. À redemander à chaque usage.
 */
export const getDeviceWhipPeerConnection = (): RTCPeerConnection | null => publishPc;

// ─── Lecture WHEP du retour programme (SFU → device) ─────────────────────────

let programPc:      RTCPeerConnection | null = null;
let programUrl:     string | null = null;
let programRetries  = 0;
let programStopped  = true;
let programOnStream: ((stream: MediaStream) => void) | null = null;

export const playProgramWhep = async (
  whepUrl: string,
  onStream: (stream: MediaStream) => void,
  addSdpLog: SdpLogger,
): Promise<void> => {
  stopProgramWhep();
  programStopped  = false;
  programUrl      = whepUrl;
  programOnStream = onStream;
  await attemptProgramPlay(addSdpLog);
};

const attemptProgramPlay = async (addSdpLog: SdpLogger): Promise<void> => {
  if (programStopped || !programUrl) return;

  console.log(`[DEVICE-WHEP] ▶ Lecture retour programme → ${programUrl}`);
  const pc = new RTCPeerConnection(defaultRTCConfig());
  programPc = pc;

  pc.addTransceiver('video', { direction: 'recvonly' });
  pc.addTransceiver('audio', { direction: 'recvonly' });

  pc.ontrack = (event) => {
    if (event.track.kind !== 'video') return;
    let stream = event.streams[0] ?? null;
    if (!stream) {
      const receivers  = pc.getReceivers();
      const videoTrack = receivers.find(r => r.track.kind === 'video')?.track;
      const audioTrack = receivers.find(r => r.track.kind === 'audio')?.track;
      stream = new MediaStream([
        ...(videoTrack ? [videoTrack] : []),
        ...(audioTrack ? [audioTrack] : []),
      ]);
    }
    console.log('[DEVICE-WHEP] 📺 Retour programme reçu');
    addSdpLog({ direction: 'received', type: 'event', label: '📺 Retour programme reçu (WHEP)' });
    programOnStream?.(stream);
  };

  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    if (pc !== programPc) return;
    if (s === 'connected') programRetries = 0;
    if (s === 'failed' || s === 'disconnected') scheduleProgramRetry(addSdpLog);
  };

  try {
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check); resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 5000);
    });

    const res = await fetch(programUrl!, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription!.sdp,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`WHEP ${res.status}${body ? ': ' + body : ''}`);
    }
    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
  } catch (err: any) {
    // 404/425 attendu tant que la régie ne publie pas encore le programme
    console.log(`[DEVICE-WHEP] ⏳ Programme indisponible: ${err?.message ?? err}`);
    try { pc.close(); } catch { /* silencieux */ }
    scheduleProgramRetry(addSdpLog);
  }
};

const scheduleProgramRetry = (addSdpLog: SdpLogger): void => {
  if (programStopped) return;
  programRetries += 1;
  const delay = Math.min(RETRY_BASE_MS * programRetries, RETRY_MAX_MS);
  setTimeout(() => { attemptProgramPlay(addSdpLog); }, delay);
};

export const stopProgramWhep = (): void => {
  programStopped = true;
  programRetries = 0;
  if (programPc) { try { programPc.close(); } catch { /* silencieux */ } programPc = null; }
  if (programUrl) { try { fetch(programUrl, { method: 'DELETE' }); } catch { /* silencieux */ } }
  programUrl      = null;
  programOnStream = null;
};
